import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Typography } from "@mui/material";

import { getEntityProfileEditRoute } from "../../../../../../routers/routeUtils";

import { useEntityAchievements } from "../../../../../Entity/api/queries/useEntityAchievements";
import { AchievementCategory } from "../../../../../Entity/types/achievements.types";

import { EditEntityProfileSectionButtonIcon } from "../../../../assets/icons/entity-icons";

import { AchievementCard } from "./AchievementCard";
import { EmptyAchievements } from "./EmptyAchievements";
import { AchievementsSkeleton } from "./AchievementsSkeleton";

const EntityAchievements = () => {
  const { t } = useTranslation("profiles", { keyPrefix: "achievements" });
  const { entityId } = useParams();
  const navigate = useNavigate();
  const handleEditEntityProfileSection = (tab: string) => navigate(getEntityProfileEditRoute(entityId!, tab));
  const { data: achievements, isLoading: isAchievementsLoading } = useEntityAchievements(entityId!);

  const achievementsList = Object.entries(achievements || {}).map(([category, achievement]) => ({
    category,
    achievement,
  }));

  const isAchievementsEmpty = (list: typeof achievementsList) => {
    return !list.length || list.every(({ achievement }) => achievement.count === 0 && achievement.imageCount === 0);
  };

  if (isAchievementsLoading) {
    return <AchievementsSkeleton />;
  }

  if (isAchievementsEmpty(achievementsList)) {
    return <EmptyAchievements />;
  }

  return (
    <Box sx={styles.container}>
      <IconButton size="small" onClick={() => handleEditEntityProfileSection("achievements")} sx={styles.editButton}>
        <EditEntityProfileSectionButtonIcon />
      </IconButton>

      <Typography sx={styles.title}>{t("achievements")}</Typography>
      <Box sx={styles.achievementsList}>
        {achievementsList.map(({ category, achievement }) => (
          <AchievementCard key={category} title={category as AchievementCategory} achievement={achievement} />
        ))}
      </Box>
    </Box>
  );
};

export default EntityAchievements;

const styles = {
  container: {
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "#FFF",
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    position: "relative",
  },
  editButton: {
    position: "absolute",
    right: "20px",
    top: "20px",
  },
  title: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "24px",
  },
  achievementsList: {
    display: "grid",
    gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(2, 1fr)" },
    gap: "8px",
  },
};
